// src/components/adjustments/StaffLoanOpeningBalanceAdjustment.jsx
//
// Adjustments -> Select Category of Adjustments -> Staff Loans.
// Unlike every other category here, there is no existing record to pick -
// this sets (or replaces) ONE balance per company, carried over from before
// Staff Loans existed in this system. Saving PUTs to
// /staff-loans/opening-balance (server/controllers/staffLoanController.js
// setOpeningBalance), which upserts by company (UNIQUE KEY company_id), so
// re-saving the same company only ever edits that one row - it never creates
// a second Staff Loan Ledger entry. The amount is signed (positive raises the
// balance, negative lowers it), the same convention Other Ledgers' Opening
// Balance already uses. It is never attached to an employee, so the Staff
// Loan Details tab (employee-wise) is never affected by it.
import React, { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import api from '../../api';
import { usePopup } from '../context/PopupContext';

const todayLocal = () => {
  const now = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
};

const fmtMoney = (value) =>
  (Number.parseFloat(value) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const fmtDate = (value) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value || ''));
  return match ? `${match[3]}/${match[2]}/${match[1]}` : '—';
};

const emptyForm = () => ({ date: todayLocal(), amount: '', remark: '' });

const StaffLoanOpeningBalanceAdjustment = () => {
  const { showPopup } = usePopup();

  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState('');
  const [openingBalances, setOpeningBalances] = useState([]); // every company that has one saved
  const [form, setForm] = useState(emptyForm());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [companiesResponse, balancesResponse] = await Promise.all([
        api.get('/companies'),
        api.get('/staff-loans/opening-balances')
      ]);
      const list = Array.isArray(companiesResponse.data) ? companiesResponse.data : [];
      setCompanies(list);
      setOpeningBalances(Array.isArray(balancesResponse.data) ? balancesResponse.data : []);
      setCompanyId((prev) => prev || (list.length > 0 ? String(list[0].id) : ''));
    } catch (loadError) {
      setError(loadError.response?.data?.error || 'Unable to load Staff Loans opening balance.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // The Company already has a saved opening balance - load it into the form
  // for editing; otherwise start blank (today + nothing entered yet).
  const existing = openingBalances.find((balance) => String(balance.company_id) === companyId) || null;
  useEffect(() => {
    setForm(existing
      ? { date: existing.date, amount: String(existing.amount), remark: existing.remark || '' }
      : emptyForm());
    setError('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const change = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const save = async (event) => {
    event.preventDefault();
    if (!companyId) return setError('Company is required.');
    if (!form.date) return setError('Date is required.');
    const text = String(form.amount).trim();
    if (!text || !/^-?\d*(\.\d{0,2})?$/.test(text) || text === '-' || text === '.') {
      return setError('Amount must be a valid number (positive or negative, at most 2 decimal places).');
    }
    const amountValue = Number(text);
    if (!Number.isFinite(amountValue)) return setError('Amount must be a valid number.');

    setSaving(true);
    setError('');
    try {
      const response = await api.put('/staff-loans/opening-balance', {
        company_id: Number(companyId),
        date: form.date,
        amount: text,
        remark: form.remark.trim() || null
      });
      const saved = response.data;
      setOpeningBalances((prev) => [
        ...prev.filter((balance) => String(balance.company_id) !== companyId),
        { company_id: saved.company_id, date: saved.date, amount: saved.amount, remark: saved.remark }
      ]);
      showPopup('success', 'Staff Loans opening balance saved successfully!');
    } catch (saveError) {
      setError(saveError.response?.data?.error || 'Unable to save Staff Loans opening balance.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <p><FontAwesomeIcon icon={faSpinner} spin /> Loading Staff Loans opening balance...</p>;
  }

  return (
    <form className="adjustments-form" onSubmit={save}>
      <h3>{existing ? 'Edit' : 'Set'} Staff Loans Opening Balance</h3>
      <div className="form-grid">
        <label className="form-group">Company <span className="required">*</span>
          <select className="form-input" value={companyId} required onChange={(event) => setCompanyId(event.target.value)}>
            <option value="" disabled>Select company</option>
            {companies.map((company) => <option key={company.id} value={String(company.id)}>{company.name}</option>)}
          </select>
        </label>
        <label className="form-group">Date <span className="required">*</span>
          <input className="form-input" type="date" value={form.date} required onChange={(event) => change('date', event.target.value)} />
          <span className="help-text">Must be earlier than every existing Staff Loan transaction for this company.</span>
        </label>
        <label className="form-group">Amount (Rs.) <span className="required">*</span>
          <input
            className="form-input"
            type="number"
            step="0.01"
            value={form.amount}
            required
            placeholder="e.g. 50000 or -50000"
            onChange={(event) => change('amount', event.target.value)}
          />
          <span className="help-text">A positive amount increases the Staff Loan balance; a negative amount decreases it.</span>
        </label>
        <label className="form-group full-width">Description / Remark
          <textarea className="form-textarea" maxLength={255} rows={3} value={form.remark} onChange={(event) => change('remark', event.target.value)} />
        </label>
      </div>

      {existing && (
        <div className="alert-message info">
          <FontAwesomeIcon icon={faInfoCircle} style={{ marginRight: '8px' }} />
          This company already has an opening balance of Rs. {fmtMoney(existing.amount)} dated {fmtDate(existing.date)}. Saving will replace it.
        </div>
      )}
      {error && <div className="alert-message error">{error}</div>}

      <div className="form-actions">
        <button type="submit" className="btn btn-primary" disabled={saving}>
          {saving ? 'Saving…' : 'Save Adjustment'}
        </button>
      </div>
    </form>
  );
};

export default StaffLoanOpeningBalanceAdjustment;
